import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { BsFileEarmarkPdf } from "react-icons/bs";
import { FiDownload, FiUpload, FiUploadCloud } from "react-icons/fi";
import { GoEye } from "react-icons/go";
import { PiSignatureDuotone } from "react-icons/pi";
import { TfiMoreAlt } from "react-icons/tfi";
import { AppEntryDocRepository } from "../repositories/AppEntryDocRepository";
import { useDispatch, useSelector } from "react-redux";
import { setFile } from "../../store/GlobalSlice";
import { notifyShowSuccessMessage } from "../../store/AlertSlice";

const options = [
  { key: "preview", label: "Преглед", icon: <GoEye size={18} /> },
  { key: "download", label: "Преземи", icon: <FiDownload size={18} /> },
  {
    key: "uploadNewDoc",
    label: "Прикачи нов",
    icon: <FiUpload size={18} />,
  },
];

export default function SignAppEntry({
  appEntry,
  docs,
  setDocs,
  setPdfBlob,
  setOpenPdfDialog,
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [menuOptions, setMenuOptions] = useState([]);
  const selectedFileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [isSignerInstalled, setIsSignerInstalled] = useState();
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setLoader(true);
    const maxWaitTime = 3000;
    const interval = setInterval(() => {
      if (window.AuctaSignerApp) {
        clearInterval(interval);
        window.AuctaSignerApp.isInstalled((status) => {
          console.log("[external page] AuctaSignerApp.isInstalled ->", status);
          if (status === "OK") {
            console.log("AuctaSigner extension is installed.");
            setIsSignerInstalled(true);
            setLoader(false);
          } else {
            console.log("AuctaSigner extension is not installed.");
            setIsSignerInstalled(false);
            setLoader(false);
          }
        });
      }
    }, 200);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!window.AuctaSignerApp) {
        console.log("AuctaSigner extension is not installed.");
      }
      setLoader(false);
    }, maxWaitTime);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if (event.source !== window) return;

      const msg = event.data;
      if (msg && typeof msg.data === "string") {
        const base64 = msg.data
          .replace(/-/g, "+")
          .replace(/_/g, "/")
          .replace(/\s+/g, "");

        try {
          const decodedJsonString = atob(base64);
          const parsed = JSON.parse(decodedJsonString);
          const pdfBase64 = parsed.message;

          const byteCharacters = atob(pdfBase64);
          const byteNumbers = new Array(byteCharacters.length)
            .fill(0)
            .map((_, i) => byteCharacters.charCodeAt(i));
          const byteArray = new Uint8Array(byteNumbers);
          const pdfBlob = new Blob([byteArray], {
            type: "application/pdf",
          });
          setPdfBlob(pdfBlob);
          setOpenPdfDialog(true);
          saveSignedDoc(pdfBase64);
          window.postMessage(
            {
              type: "CLOSE_POPUP",
            },
            "*"
          );
        } catch (err) {
          console.error("Failed to decode or parse the message:", err);
        }
      } else {
        console.log("Unexpected message format:", msg);
      }
    };

    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
    };
  }, []);

  const uploadNewDoc = () => {
    setSaving(true);
    // Create a hidden file input element and click
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".pdf,.dwg,.png,.jpg,.jpeg";
    fileInput.style.display = "none";
    document.body.appendChild(fileInput);
    fileInput.click();

    fileInput.onchange = () => {
      const file = fileInput.files[0];
      const formData = new FormData();
      formData.append("file", file);
      formData.append("appEntryId", appEntry?.id);

      AppEntryDocRepository.uploadNewApprovalRequest(formData)
        .then((res) => {
          setDocs(res.data);
          setSaving(false);
          dispatch(
            notifyShowSuccessMessage(
              "Успешно е прикачен нов документ и заменет со постоечкиот генериран документ."
            )
          );
        })
        .catch((err) => {
          console.error("Error uploading document:", err);
          setSaving(false);
        });
    };

    fileInput.remove();
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedFile(null);
  };

  const handleMenuClick = (event, fileName) => {
    setMenuAnchor(event.currentTarget);
    setSelectedFile(fileName);

    const filtered = options.filter((option) => {
      if (fileName.includes("-signed") && option.key === "sign") {
        return false;
      }
      return true;
    });
    setMenuOptions(filtered);
  };

  const signFileByName = (fileName) => {
    AppEntryDocRepository.getDocument(appEntry?.id, fileName, {
      responseType: "blob",
    })
      .then(async (res) => {
        const blob = res.data;
        setSelectedFile(fileName);
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64data = reader.result.split(",")[1];
            resolve(base64data);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        window.postMessage(
          {
            type: "FILE_UPLOAD",
            action: "WITH_PREVIEW",
            subaction: "SILENT_SIGN_PDF",
            fileName: fileName,
            fileType: "application/pdf",
            fileContent: base64,
          },
          "*"
        );
      })
      .catch((err) => {
        console.error("Error signing file:", err);
      });
  };

  const getByFileName = (fileName) => {
    AppEntryDocRepository.getDocument(appEntry?.id, fileName, {
      responseType: "blob",
    })
      .then((res) => {
        setPdfBlob(res.data);
        setOpenPdfDialog(true);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const downloadByFileName = (fileName) => {
    setSaving(true);
    AppEntryDocRepository.getDocument(appEntry?.id, fileName, {
      responseType: "blob",
    })
      .then((res) => {
        setSaving(false);
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        setSaving(false);
        console.log(err);
      });
  };

  const saveSignedDoc = (pdfBase64) => {
    AppEntryDocRepository.saveSignedDoc(
      pdfBase64,
      appEntry?.id,
      selectedFileRef.current
    )
      .then((res) => {
        setDocs(res.data);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const unsignedDocs = (docs || []).filter((r) => !r.includes("-signed"));
  const signedDocs = (docs || []).filter((r) => r.includes("-signed"));

  const renderReportItem = (r, isSigned = false) => {
    let displayName = "";
    if (r.includes("temporaryTrafficChangeRequest")) {
      displayName =
        "Барање за согласност за времена измена на режим на сообраќај";
    } else if (r.includes("permanentTrafficChangeRequest")) {
      displayName =
        "Барање за согласност за трајна измена на режим на сообраќај";
    }

    if (isSigned) {
      displayName = `Потпишано ${displayName}`;
    }

    return (
      <>
        {!isSignerInstalled && !loader && (
          <Alert severity="error" variant="filled" sx={{ padding: "4px 8px" }}>
            За потпишување на документите, потребно е да ја инсталирате
            екстензијата за потпишување.{" "}
            <a
              target="_blank"
              style={{ color: "white", fontWeight: "bold" }}
              href="https://chromewebstore.google.com/detail/aucta-signer-v2/gdekohcdoabefpfpjcanibkkhfinifmn"
            >
              Преземете ја екстензијата тука
            </a>
            <br />
            По инсталирањето на екстензијата освежете ја страницата{" "}
            <a
              href="#"
              style={{ color: "white", fontWeight: "bold" }}
              onClick={() => window.location.reload()}
            >
              тука
            </a>
            .
          </Alert>
        )}

        <Box
          key={r}
          sx={{
            border: "1px solid #e0e0e0",
            padding: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "5px",
            backgroundColor: "#fff",
          }}
        >
          {saving && (
            <Backdrop open={true} sx={{ zIndex: 9999, color: "#fff" }}>
              <CircularProgress color="inherit" />
            </Backdrop>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <div
              style={{
                padding: "2px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
              }}
            >
              <BsFileEarmarkPdf
                size={25}
                color="#0070cc"
                style={{ padding: 0 }}
              />
            </div>
            <Typography
              key={r}
              variant="subtitle1"
              style={{
                fontSize: "14px",
                lineHeight: 1,
              }}
            >
              {displayName}
            </Typography>
          </div>

          <div style={{ gap: "10px", display: "flex" }}>
            {!isSigned && signedDocs.length === 0 && isSignerInstalled && (
              <Button
                variant="outlined"
                size="large"
                sx={{
                  border: "1px solid #e1e1e1",
                  display: "inline-flex",
                  color: theme.palette.secondary.main,
                  borderRadius: 0,
                  textTransform: "none",
                  fontSize: 15,
                  fontFamily: "Inter-Regular",
                  justifyContent: "center",
                  alignItems: "center",
                  "&:hover": {
                    backgroundColor: "#eff5fb",
                    border: "1px solid #eff5fb",
                    color: theme.palette.secondary.main,
                  },
                }}
                disabled={!isSignerInstalled}
                onClick={() => {
                  signFileByName(r);
                  selectedFileRef.current = r;
                }}
                endIcon={
                  <PiSignatureDuotone
                    size={18}
                    color={theme.palette.primary.main}
                  />
                }
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontFamily: "Inter-Regular",
                    marginLeft: "2px",
                    color: theme.palette.secondary.main,
                  }}
                >
                  Потпиши
                </Typography>
              </Button>
            )}
            <IconButton
              disableRipple
              aria-label="more"
              id="long-button"
              aria-controls={open ? "long-menu" : undefined}
              aria-expanded={open ? "true" : undefined}
              aria-haspopup="true"
              onClick={(e) => handleMenuClick(e, r)}
              sx={{
                border: "1px solid #f3f5f5",
                backgroundColor: "#f7f8f8",
                borderRadius: 1,
              }}
            >
              <TfiMoreAlt size={16} color={theme.palette.secondary.main} />
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={menuAnchor}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
              slotProps={{
                paper: {
                  style: {
                    width: "20ch",
                    boxShadow:
                      "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
                  },
                },
                list: {
                  "aria-labelledby": "long-button",
                },
              }}
            >
              {menuOptions.map((option) => (
                <MenuItem
                  key={option.label}
                  selected={option === "Pyxis"}
                  disableRipple
                  onClick={() => {
                    if (option.key === "preview") {
                      getByFileName(selectedFile);
                    } else if (option.key === "download") {
                      downloadByFileName(selectedFile);
                    } else if (option.key === "sign") {
                      signFileByName(selectedFile);
                    } else if (option.key === "uploadNewDoc") {
                      uploadNewDoc();
                    }
                    handleMenuClose();
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "10px",
                    margin: "1px 5px",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: "#eaf5ff",
                    },
                  }}
                >
                  {option.icon}
                  <Typography variant="subtitle2" style={{ fontSize: "14px" }}>
                    {option.label}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </div>
        </Box>
      </>
    );
  };

  return (
    <Grid item xs={12} style={{ marginTop: "20px" }}>
      <Grid
        container
        spacing={1}
        style={{
          padding: "20px",
          gap: "10px",
        }}
      >
        <Grid
          item
          xs={5.9}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "5px",
            backgroundColor: "#fafafa",
            padding: "10px",
          }}
        >
          <Typography
            variant="h6"
            style={{ color: theme.palette.secondary.main }}
          >
            Генерирани барања
          </Typography>
          {unsignedDocs.length > 0 ? (
            unsignedDocs.map((r) => renderReportItem(r))
          ) : (
            <Typography variant="body1">Нема генерирани барања.</Typography>
          )}
        </Grid>
        <Grid
          item
          xs={6}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "5px",
            backgroundColor: "#fafafa",
            padding: "10px",
          }}
        >
          <Typography
            variant="h6"
            style={{ color: theme.palette.secondary.main }}
          >
            Потпишани барања
          </Typography>
          {signedDocs.length > 0 ? (
            signedDocs.map((r) => renderReportItem(r, true))
          ) : (
            <Typography variant="body1">Нема потпишани барања.</Typography>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
